import {
  faAngleRight,
  faCheck,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  DialogContent,
  DialogTitle,
  Modal,
  ModalDialog,
  useTheme,
} from '@mui/joy';
import React from 'react';
import ErrorMessage from '../error-message/error-message';
import { ProjectReleaseStatusData, ReleaseStatus } from '../../types/project';
import { getNextReleaseStatusData } from './approve-release-confirmation-modal.helper';
import AnimatedCheckCircle from '../animated-check-circle/animated-check-circle';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import Message from '../message/message';
import { Trans, useTranslation } from 'react-i18next';

export type ApproveReleaseConfirmationModalProps = {
  isOpen: boolean;
  handleCancel: () => void;
  handleConfirm: () => void;
  handleConfirmationClose?: () => void;
  releaseName?: string;
  status?: ProjectReleaseStatusData;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  showConfirmation?: boolean;
  gitUrl?: string;
  allReviewed?: boolean;
  allTranslated?: boolean;
};

const ApproveReleaseConfirmationModal = ({
  isOpen,
  handleCancel,
  handleConfirm,
  handleConfirmationClose,
  status,
  releaseName,
  isLoading,
  isError,
  error,
  showConfirmation,
  gitUrl,
  allReviewed,
  allTranslated,
}: ApproveReleaseConfirmationModalProps) => {
  const joyUiTheme = useTheme();
  const { t } = useTranslation();

  const nextReleaseStatus = React.useMemo(
    () => getNextReleaseStatusData(status?.code),
    [status],
  );

  const handleGithubNav = () => {
    if (gitUrl) {
      window.open(gitUrl, '_blank');
    }
  };

  return (
    <Modal open={isOpen}>
      <ModalDialog minWidth={'500px'} maxWidth={'500px'}>
        <DialogTitle level='h3'>{t('approve-release.title')}</DialogTitle>
        <DialogContent>
          {showConfirmation ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <h3 style={{ margin: 0 }}>{t('common.success')}</h3>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <AnimatedCheckCircle />
              </div>
              <div style={{ textAlign: 'center' }}>
                <b>{releaseName}</b>
                {` ${t('approve-release.success-relsd')}`}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '16px',
                  gap: '16px',
                }}
              >
                <Button
                  startDecorator={<FontAwesomeIcon icon={faGithub} />}
                  onClick={handleGithubNav}
                  color='neutral'
                >
                  {t('approve-release.github')}
                </Button>
                <Button
                  onClick={handleConfirmationClose}
                  startDecorator={<FontAwesomeIcon icon={faAngleRight} />}
                >
                  {t('approve-release.return')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  margin: '16px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {status?.code === ReleaseStatus.ClientReviewPostTranslation ? (
                  <>
                    <div>
                      {t('approve-release.confirmation.relsd')}{' '}
                      <b>{releaseName}</b>?
                    </div>
                    <div>{t('approve-release.confirmation.git')}</div>
                  </>
                ) : (
                  <div>
                    {t('approve-release.confirmation.non-relsd')}{' '}
                    <b>{releaseName}</b> {t('approve-release.confirmation.to')}{' '}
                    <b>
                      {nextReleaseStatus?.description
                        ? t(nextReleaseStatus?.description)
                        : 'undefined'}
                    </b>
                    ?
                  </div>
                )}

                <div
                  style={{
                    color: joyUiTheme.colorSchemes.light.palette.danger[500],
                  }}
                >
                  <Trans
                    i18nKey='common.cannot-undo'
                    components={{ b: <b /> }}
                  />
                </div>
                {!allTranslated &&
                  status?.code ===
                    ReleaseStatus.ClientReviewPostTranslation && (
                    <Message
                      variant='warning'
                      title={t('approve-release.translate-warn.heading')}
                    >
                      <div>{t('approve-release.translate-warn.body')}</div>
                      <div style={{ fontSize: '14px' }}>
                        {t('approve-release.translate-warn.info')}
                      </div>
                    </Message>
                  )}
                {!allReviewed &&
                  (status?.code === ReleaseStatus.ClientReviewPreTranslation ||
                    status?.code ===
                      ReleaseStatus.ClientReviewPostTranslation) && (
                    <Message
                      variant='warning'
                      title={t('approve-release.review-warn.heading')}
                    >
                      {t('approve-release.review-warn.body')}
                    </Message>
                  )}
                <ErrorMessage
                  isError={isError}
                  error={error}
                  retry={handleConfirm}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '16px',
                }}
              >
                <Button
                  startDecorator={<FontAwesomeIcon icon={faXmark} />}
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  color='success'
                  startDecorator={<FontAwesomeIcon icon={faCheck} />}
                  onClick={handleConfirm}
                  loading={isLoading}
                >
                  {t('common.confirm')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default ApproveReleaseConfirmationModal;
